<!--倒计时-->
<template>
  <span :style="{ fontSize: fontSize + 'px', color: 'red' }">{{ currentDate || timeText }}</span>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import { Modal, message } from 'ant-design-vue';

import { getOrderStatus, getContractStatus, Status } from '@/apis';
import { ORDER_STATUS } from '@/apis/common';
import { OrderInfo, ORDER_PAGE_STATUS } from '../utils/config';
import { getCurrentTime, handleResultStatus } from '../utils/method';

const props = defineProps({
  lastTime: {
    type: Number,
    required: true,
  },
  currentTime: {
    type: Number,
    required: true,
  },
  changeTimeoutStatus: {
    type: Function as PropType<(status: boolean) => void>,
    required: true,
  },
  fontSize: {
    type: Number,
    required: false,
    default: 20,
  },
  orderData: {
    type: (Object as PropType<OrderInfo>) || undefined,
    required: true,
  },
  changePaymentStatus: {
    type: Function as PropType<(status: ORDER_PAGE_STATUS) => void>,
    required: true,
  },
  timeText: {
    type: String,
    required: true,
  },
});

const currentDate = ref<string>('');
const { orderData, currentTime, lastTime, changePaymentStatus, changeTimeoutStatus } = props;

// 计算时间差
const timestampDiff = currentTime - Date.now();

// 限制重试次数，避免无限递归
let retryCount = 0;
const MAX_RETRY = 3;

// 标记是否已弹出超时提示
let hasShownTimeoutModal = false;

const handleModal = () => {
  Modal.error({
    title: '支付超时',
    content: '订单支付超时，请重新下单',
    okText: '知道了',
    onOk: () => {
      handleResultStatus(orderData.failRedirect);
    },
  });
};

// 倒计时结束后，查询订单状态
const getOrderStatusFn = async () => {
  try {
    const { data } = await getOrderStatus({
      orderNo: orderData.orderNo,
    });

    if (data) {
      if (data?.orderStatus === ORDER_STATUS.PAID) {
        message.success('订单已支付');
        changePaymentStatus(ORDER_PAGE_STATUS.PAID);
      } else if (data?.orderStatus === ORDER_STATUS.CANCELLED) {
        handleModal();
      } else {
        // 如果远端状态更改不及时，容错处理，延迟后再次请求
        retryCount++;
        if (retryCount < MAX_RETRY) {
          setTimeout(() => {
            getOrderStatusFn();
          }, 2000); // 延迟 2 秒后重试
        } else {
          // 超过重试次数，直接显示超时弹窗
          handleModal();
        }
      }
    }
  } catch (error) {
    console.error('查询订单状态失败:', error);
    // 接口失败也弹出超时提示
    handleModal();
  }
};

const updateCountdown = () => {
  const nowTimestamp = Date.now() + timestampDiff;
  const remainingTime = props.lastTime - nowTimestamp; // 计算剩余时间戳

  // ✅ 剩余 1 秒时，弹出超时提示并禁用按钮
  if (remainingTime <= 1000 && remainingTime > 0 && !hasShownTimeoutModal) {
    hasShownTimeoutModal = true;
    changeTimeoutStatus(true); // 立即禁用支付按钮
    handleModal(); // 弹出超时提示
  }

  if (remainingTime < 1000) {
    currentDate.value = '00:00:00';
    clearInterval(timer);
    changeTimeoutStatus(true); // 确保按钮禁用

    // 如果还没弹出过提示，现在查询订单状态或弹出提示
    if (!hasShownTimeoutModal) {
      hasShownTimeoutModal = true; // 防止重复操作
      // 区分普通订单还是签约
      orderData.orderNo !== 'null' ? getOrderStatusFn() : handleModal();
    }
    return;
  }

  currentDate.value = getCurrentTime(remainingTime);
};

const timer = setInterval(updateCountdown, 1000);
</script>
